import { Box, Stack, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import type { PortfolioEvaluationWarningItem } from "Infrastructure/ApiZfp/Schema/portfolioEvaluationWarningItem";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

const RISK_SCALE_STEPS = 7;

type Props = {
  readonly riskItem: PortfolioEvaluationWarningItem;
};

export const InvestmentsNsureRiskProfile = ({ riskItem }: Props) => {
  const { t } = useResource();

  const clientRiskProfile = Number.parseFloat(riskItem.clientEvaluationValue ?? "");
  const hasScale = Number.isFinite(clientRiskProfile) && clientRiskProfile > 0;

  return (
    <Stack spacing={Spacing.M}>
      <Flex justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={600}>
          {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.RiskProfile)}
        </Typography>
        {hasScale && (
          <Typography variant="caption" color="text.secondary">
            {clientRiskProfile} / {RISK_SCALE_STEPS}
          </Typography>
        )}
      </Flex>

      {hasScale && <RiskScale value={clientRiskProfile} />}

      <Stack spacing={Spacing.XS} pt={Spacing.XS}>
        <RiskValueRow
          label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.RiskClientProfile)}
          value={riskItem.clientEvaluationValue}
        />
        <RiskValueRow label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.RiskInvestment)} value={riskItem.toolValue} />
      </Stack>
    </Stack>
  );
};

const RiskScale = ({ value }: { readonly value: number }) => {
  const activeSteps = Math.min(Math.round(value), RISK_SCALE_STEPS);

  return (
    <Flex gap="3px">
      {Array.from({ length: RISK_SCALE_STEPS }, (_, index) => index + 1).map((step) => (
        <Box key={step} flex={1} height="6px" borderRadius={Radius.S} bgcolor={step <= activeSteps ? "primary.main" : "grey.200"} />
      ))}
    </Flex>
  );
};

const RiskValueRow = ({ label, value }: { readonly label: string; readonly value?: string | null }) => (
  <Flex justifyContent="space-between" alignItems="center" gap={Spacing.S}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="caption" fontWeight={600}>
      {value ?? "—"}
    </Typography>
  </Flex>
);
